import { logEvent as fsLogEvent, identify as fsIdentify } from "@fullscript/fs-analytics";
import type { Identify } from "@fullscript/fs-analytics/rudderTyper";
import { useEffect, useRef, useCallback } from "react";

import { useFullStorySessionUrl } from "@shared/hooks/useFullStorySessionUrl/useFullStorySessionUrl";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";

import { useFsAnalyticsUserSharedQuery } from "./FsAnalyticsUser.query.generated";

const getProfileId = (
  preparedUserId: string | null | undefined,
  role: string | null | undefined
): string | undefined => {
  const userId = preparedUserId?.trim();
  const normalizedRole = role?.trim().toLowerCase();

  if (!userId || !normalizedRole || normalizedRole === "roleless") {
    return undefined;
  }

  const suffix = normalizedRole === "patient" ? "PATIENT" : "STAFF";

  return `${userId}_${suffix}`;
};

const useFsAnalytics = () => {
  const { isAdminImpersonating, storePlatform, isUnauthenticated } = useSharedGlobalConfig();
  const fullStorySessionUrl = useFullStorySessionUrl();
  const {
    data: userData,
    loading,
    error,
  } = useFsAnalyticsUserSharedQuery({
    skip: isUnauthenticated,
  });
  const isReady = (!loading && !error) || isUnauthenticated;
  const roleType = userData?.currentUser?.currentUserRoleType?.trim();

  const getIdentifyTraits = useCallback(
    (): Record<string, unknown> => ({
      email: userData?.currentUser?.email,
      role_type: roleType,
      environment: userData?.analyticsEnvironment,
      impersonating: isAdminImpersonating,
      store_platform: storePlatform,
    }),
    [userData, roleType, isAdminImpersonating, storePlatform]
  );

  const getContextFields = useCallback(
    (preparedUserIdOverride?: string | null): Record<string, unknown> => {
      const preparedUserId = userData?.preparedUserId ?? preparedUserIdOverride;
      const profileId = getProfileId(preparedUserId, roleType);
      const currentStore = userData?.currentStore;
      const hipaaConsentGiven =
        userData?.currentPatient?.personalization?.hasFullscriptPersonalizationConsent;

      return {
        account_modality: currentStore?.accountModality ?? null,
        fullstory_session_url: fullStorySessionUrl,
        ...(typeof hipaaConsentGiven === "boolean"
          ? { hipaa_consent_given: hipaaConsentGiven }
          : {}),
        is_internal_store: userData?.currentStore?.isInternal ?? false,
        organization_id: userData?.preparedOrganizationId,
        practitioner_modality: userData?.currentPractitioner?.practitionerType?.description,
        profile_id: profileId,
        role_id: userData?.preparedRoleId,
        store_id: userData?.preparedStoreId,
      };
    },
    [userData, roleType, fullStorySessionUrl]
  );

  const getIdentifyContextFields = useCallback(
    (preparedUserIdOverride?: string | null): Record<string, unknown> => {
      const preparedUserId = (userData?.preparedUserId ?? preparedUserIdOverride)?.trim();

      return {
        ...getContextFields(preparedUserIdOverride),
        user_id: preparedUserId,
      };
    },
    [userData, getContextFields]
  );

  const getEventPayload = useCallback(
    (): Record<string, unknown> => ({
      ...getIdentifyTraits(),
      ...getContextFields(),
    }),
    [getIdentifyTraits, getContextFields]
  );

  type QueuedEvent = {
    type: "event";
    fn: keyof ReturnType<typeof fsLogEvent>;
    payload?: Record<string, unknown>;
  };
  type QueuedIdentify = { type: "identify"; preparedUserId?: string | null };
  const queue = useRef<(QueuedEvent | QueuedIdentify)[]>([]);

  useEffect(() => {
    if (!isReady || queue.current.length === 0) return;

    const eventHandlers = fsLogEvent();
    const identifyTraits = getIdentifyTraits();
    const eventPayload = getEventPayload();

    queue.current.forEach(item => {
      if (item.type === "event") {
        (eventHandlers[item.fn] as (p: Record<string, unknown>) => void)({
          ...eventPayload,
          ...item.payload,
        });
      } else if (item.type === "identify" && roleType) {
        const userId = (userData?.preparedUserId ?? item.preparedUserId?.trim()) || "";
        if (!userId) return;

        const profileId = getProfileId(userId, roleType);

        if (profileId) {
          fsIdentify(profileId, {
            ...identifyTraits,
            ...getIdentifyContextFields(userId),
          } as unknown as Identify);
        }
      }
    });

    queue.current = [];
  }, [isReady, userData, roleType, getEventPayload, getIdentifyContextFields, getIdentifyTraits]);

  const logEvent = () => {
    let eventHandlers: ReturnType<typeof fsLogEvent>;
    try {
      eventHandlers = fsLogEvent();
    } catch {
      return new Proxy({} as ReturnType<typeof fsLogEvent>, {
        get: () => () => {},
      });
    }

    return new Proxy(eventHandlers, {
      get: (target, prop) => {
        const originalFn = target[prop as keyof typeof target];
        if (typeof originalFn === "function") {
          return (payload: Record<string, unknown> = {}) => {
            if (isReady) {
              return (originalFn as (p: Record<string, unknown>) => void)({
                ...getEventPayload(),
                ...payload,
              });
            }
            queue.current.push({ type: "event", fn: prop as keyof typeof fsLogEvent, payload });
          };
        }

        return originalFn;
      },
    });
  };

  const identify = (preparedUserId?: string | null) => {
    try {
      if (!isReady) {
        queue.current.push({ type: "identify", preparedUserId });

        return;
      }

      const userId = (userData?.preparedUserId ?? preparedUserId?.trim()) || "";
      if (!roleType || !userId) return;

      const profileId = getProfileId(userId, roleType);

      if (profileId) {
        fsIdentify(profileId, {
          ...getIdentifyTraits(),
          ...getIdentifyContextFields(userId),
        } as unknown as Identify);
      }
    } catch {
      // no-op
    }
  };

  return { logEvent, identify, isReady };
};

export { useFsAnalytics };
